import {
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    Input,
    notification,
    Row,
    Select,
} from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { issuePriorities, getUserRole } from "../../services/util";
import "./TicketForm.css";

dayjs.extend(customParseFormat);

const { Option } = Select;

function TicketForm() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const hotel_assigned = localStorage.getItem("hotel_assigned");
    const [form] = Form.useForm();
    const [submitLoading, setSubmitLoading] = useState(false);
    const { loggedIn } = useContext(SettingContext);
    const [api, contextHolder] = notification.useNotification();
    const navigate = useNavigate();
    const userRoleId = localStorage.getItem("userRoleId");
    const userRole = getUserRole(userRoleId);

    useEffect(() => {
        if (!loggedIn) {
            navigate("/login");
            return null;
        }
        if (userRole === "External Vendor" && hotel_assigned === "false") {
            api["warning"]({
                message: <strong>No hotel access</strong>,
                description:
                    "You do not have any hotel assigned. Contact your admin for assistance.",
            });
        }
    }, [loggedIn]);

    const {
        status: hotelAffectedOptionStatus,
        data: hotelAffectedOptionRes,
        refetch: getHotelsAffectedOptions,
    } = useQuery(
        "affectedHotels",
        async () => {
            return await HTTP("/dashboard/getJiraHotelsAffected", { staff_id });
        },
        { enabled: false },
    );

    const {
        status: hotelStatus,
        data: hotelData,
        refetch: getHotels,
    } = useQuery(
        "hotels",
        async () => {
            return await HTTP("/dashboard/getHotels", {
                staff_id,
                hotel_type: "all",
            });
        },
        { enabled: false },
    );

    const {
        status: componentStatus,
        data: componentsData,
        refetch: getComponents,
    } = useQuery(
        "hubspot-components",
        async () => {
            return await HTTP("/dashboard/getJiraComponents", { staff_id });
        },
        { enabled: false },
    );

    const {
        status: jiraAssigneeStatus,
        data: jiraAssigneeData,
        refetch: getJiraAssignee,
    } = useQuery(
        "jira-users",
        async () => {
            return await HTTP("/dashboard/getJiraUsers", { staff_id });
        },
        { enabled: false },
    );

    useEffect(() => {
        getHotels();
        getComponents();
        getJiraAssignee();
        getHotelsAffectedOptions();
    }, []);

    const jiraAssignees = Array.isArray(jiraAssigneeData)
        ? jiraAssigneeData
        : jiraAssigneeData?.data || [];

    async function handleSubmit(values) {
        setSubmitLoading(true);
        values.incident_time = dayjs(values.incident_time).format(
            "YYYY/MM/DD HH:MM",
        );

        const formData = {
            staff_id,
            hubspot_ticket: false,
            jira_ticket: true,
            ...values,
        };

        const { success, data, message } = await HTTP(
            "/dashboard/createHubspotTicket",
            formData,
        );

        if (success === 0) {
            notification["error"]({ message });
        } else {
            if (
                userRole !== "External Vendor" &&
                userRole !== "Customer" &&
                data[0].key &&
                data[0].id &&
                data[0].self
            ) {
                api.open({
                    message,
                    description: (
                        <p>
                            You can view it at&nbsp;
                            <a
                                href={`https://astics.atlassian.net/browse/${data[0].key}`}
                            >
                                {data[0].key}
                            </a>
                            .
                        </p>
                    ),
                });
            } else {
                notification["info"]({ message });
            }
        }

        setSubmitLoading(false);
        form.resetFields();
    }

    return (
        <div className="ticket-form-page">
            {contextHolder}
            <Row justify="center" className="ticket-form-row">
                <Col xs={24} sm={22} md={18} lg={14} xl={12}>
                    <div className="ticket-form-header">
                        <span className="ticket-form-badge">Jira</span>
                        <h1 className="ticket-form-title">Create Ticket</h1>
                        <p className="ticket-form-subtitle">
                            Fill in the details below to raise a new support
                            ticket.
                        </p>
                    </div>

                    <Card className="ticket-card" bordered={false}>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmit}
                            initialValues={{
                                hubspot_ticket: false,
                                jira_ticket: false,
                            }}
                            className="ticket-form"
                        >
                            {/* Row 1: Subject + Content */}
                            <div className="form-section">
                                <p className="form-section-label">Basic Info</p>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            name="subject"
                                            label="Subject"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Please enter a subject.",
                                                },
                                            ]}
                                        >
                                            <Input placeholder="e.g. Login page not loading" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            name="content"
                                            label="Content"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Please enter content.",
                                                },
                                            ]}
                                        >
                                            <Input placeholder="Describe the issue briefly" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Row 2: Hotel + Property ID */}
                            <div className="form-section">
                                <p className="form-section-label">Location</p>
                                <Row gutter={16}>
                                    <Col xs={24} md={16}>
                                        <Form.Item
                                            label="Hotel Name"
                                            name="hotel_id"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Please select a hotel.",
                                                },
                                            ]}
                                            valuePropName="defaultValue"
                                        >
                                            <Select
                                                showSearch
                                                placeholder="Search hotel..."
                                                filterOption={(input, option) =>
                                                    option.children
                                                        .toLowerCase()
                                                        .includes(
                                                            input.toLowerCase(),
                                                        )
                                                }
                                            >
                                                {hotelStatus === "success" &&
                                                hotelData?.data?.length > 0
                                                    ? hotelData.data.map(
                                                          (h) => (
                                                              <Option
                                                                  key={
                                                                      h.hotel_id
                                                                  }
                                                                  value={
                                                                      h.hotel_id
                                                                  }
                                                              >
                                                                  {`${h.hotel_id} – ${h.hotel_name} – ${h.city}`}
                                                              </Option>
                                                          ),
                                                      )
                                                    : null}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={8}>
                                        <Form.Item
                                            name="property_id"
                                            label="Property ID"
                                            rules={[{ required: false }]}
                                        >
                                            <Input placeholder="Optional" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Row 3: Component + Priority */}
                            <div className="form-section">
                                <p className="form-section-label">
                                    Classification
                                </p>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Component"
                                            name="component_name"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Please select a component.",
                                                },
                                            ]}
                                            valuePropName="defaultValue"
                                        >
                                            <Select
                                                placeholder="Select component"
                                                loading={
                                                    componentsData?.data
                                                        ?.length === 0
                                                }
                                            >
                                                {componentStatus ===
                                                    "success" &&
                                                componentsData?.data.length > 0
                                                    ? componentsData.data.map(
                                                          (s) => (
                                                              <Option
                                                                  key={
                                                                      s.component_name
                                                                  }
                                                                  value={
                                                                      s.component_name
                                                                  }
                                                              >
                                                                  {
                                                                      s.component_name
                                                                  }
                                                              </Option>
                                                          ),
                                                      )
                                                    : null}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Priority"
                                            name="priority"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Please select a priority.",
                                                },
                                            ]}
                                        >
                                            <Select placeholder="Select priority">
                                                {issuePriorities.map((p) => (
                                                    <Option key={p} value={p}>
                                                        <span
                                                            className={`priority-dot priority-${p.toLowerCase()}`}
                                                        />
                                                        {p}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Row 4: Assignee + Affected Hotel (internal only) */}
                            {userRole !== "External Vendor" &&
                                userRole !== "Customer" && (
                                    <div className="form-section">
                                        <p className="form-section-label">
                                            Assignment
                                        </p>
                                        <Row gutter={16}>
                                            <Col xs={24} md={12}>
                                                <Form.Item
                                                    label="Assignee"
                                                    name="assignee_id"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message:
                                                                "Please select an assignee.",
                                                        },
                                                    ]}
                                                    valuePropName="defaultValue"
                                                >
                                                    <Select
                                                        showSearch
                                                        placeholder="Search assignee..."
                                                        loading={
                                                            jiraAssigneeStatus ===
                                                                "loading" ||
                                                            jiraAssigneeStatus ===
                                                                "idle"
                                                        }
                                                        filterOption={(
                                                            input,
                                                            option,
                                                        ) =>
                                                            option.children
                                                                .toLowerCase()
                                                                .includes(
                                                                    input.toLowerCase(),
                                                                )
                                                        }
                                                    >
                                                        {jiraAssigneeStatus ===
                                                            "success" &&
                                                        jiraAssignees.length > 0
                                                            ? jiraAssignees.map(
                                                                  (u) => (
                                                                      <Option
                                                                          key={
                                                                              u.account_id
                                                                          }
                                                                          value={
                                                                              u.account_id
                                                                          }
                                                                      >
                                                                          {
                                                                              u.display_name
                                                                          }
                                                                      </Option>
                                                                  ),
                                                              )
                                                            : null}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={12}>
                                                <Form.Item
                                                    label="Affected Hotel"
                                                    name="hotel_affected"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message:
                                                                "Please select affected hotel.",
                                                        },
                                                    ]}
                                                    valuePropName="defaultValue"
                                                >
                                                    <Select
                                                        showSearch
                                                        placeholder="Search affected hotel..."
                                                        loading={
                                                            hotelAffectedOptionRes
                                                                ?.data
                                                                ?.length === 0
                                                        }
                                                        filterOption={(
                                                            input,
                                                            option,
                                                        ) =>
                                                            option.children
                                                                .toLowerCase()
                                                                .includes(
                                                                    input.toLowerCase(),
                                                                )
                                                        }
                                                    >
                                                        {hotelAffectedOptionStatus ===
                                                            "success" &&
                                                            hotelAffectedOptionRes
                                                                ?.data?.length >
                                                                0 &&
                                                            hotelAffectedOptionRes.data.map(
                                                                (h) => (
                                                                    <Option
                                                                        key={
                                                                            h.hotel_affected
                                                                        }
                                                                        value={
                                                                            h.hotel_affected
                                                                        }
                                                                    >
                                                                        {
                                                                            h.hotel_affected
                                                                        }
                                                                    </Option>
                                                                ),
                                                            )}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>
                                )}

                            {/* Row 5: Incident Date-Time */}
                            <div className="form-section">
                                <p className="form-section-label">
                                    Incident Details
                                </p>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Incident Date & Time"
                                            name="incident_time"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Please select date & time.",
                                                },
                                            ]}
                                        >
                                            <DatePicker
                                                allowClear={false}
                                                showTime
                                                format="MM/DD/YYYY hh:mm"
                                                style={{ width: "100%" }}
                                                disabledDate={(current) =>
                                                    current &&
                                                    current >
                                                        dayjs().endOf("day")
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </div>

                            {/* Submit */}
                            <div className="form-actions">
                                <Button
                                    className="ticket-submit-btn"
                                    type="primary"
                                    htmlType="submit"
                                    loading={submitLoading}
                                    size="large"
                                >
                                    {submitLoading
                                        ? "Creating…"
                                        : "Create Ticket"}
                                </Button>
                            </div>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export { TicketForm };
